import { DefaultButton, Icon } from "@fluentui/react";
import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ERROR_BUTTON_STYLES, ERROR_STYLES } from "../../Styles";
import { ErrorDisplayProps } from "../../Interfaces";
import { DEFAULT_ERROR_MESSAGE, TROUBLESHOOTING_TIPS, generateTipKey } from "../../Utils";
import { SecureAnchor } from "./SecureAnchor";

/**
 * A reusable error display component for the AI Playground.
 * Provides a consistent, accessible error UI with support contact options and troubleshooting guidance.
 */

export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  errorMessage,
  showTroubleshooting = true,
  supportEmail = "security360core@microsoft.com"
}) => {
  const supportMailto = useMemo(
    () => `mailto:${supportEmail}?subject=AI Playground Error&body=Please describe the issue you encountered.`,
    [supportEmail]
  );

  const displayMessage = errorMessage || DEFAULT_ERROR_MESSAGE;

  // Memoized troubleshooting tips with proper keys for better performance
  const troubleshootingItems = useMemo(
    () => TROUBLESHOOTING_TIPS.map((tip, index) => <li key={generateTipKey(tip, index)}>{tip}</li>),
    []
  );

  return (
    <div
      role="alert"
      aria-live="assertive"
      aria-label="Error notification with support contact options"
      style={ERROR_STYLES.container}
    >
      {/* Error Header */}
      <div style={ERROR_STYLES.header}>
        <Icon iconName="Error" style={ERROR_STYLES.icon} aria-hidden="true" />
        <span>Something went wrong</span>
      </div>

      {/* Error Message */}
      <div style={ERROR_STYLES.message}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            // Ensure links in error messages are accessible
            a: SecureAnchor
          }}
        >
          {displayMessage}
        </ReactMarkdown>
      </div>

      {/* Contact Support */}
      <DefaultButton
        ariaLabel="Contact support team via email"
        href={supportMailto}
        iconProps={{ iconName: "Help" }}
        rel="noopener noreferrer"
        styles={ERROR_BUTTON_STYLES.secondary}
        target="_blank"
        text="Contact Support"
        title="Send an email to the support team for assistance"
      />

      {/* Troubleshooting Tips */}
      {showTroubleshooting && (
        <div style={ERROR_STYLES.troubleshooting}>
          <strong>Troubleshooting tips:</strong>
          <ul style={ERROR_STYLES.list}>{troubleshootingItems}</ul>
        </div>
      )}
    </div>
  );
};
